import { useMutation, useQuery } from "@apollo/client";
import { Button, ButtonGroup, Card } from "@blueprintjs/core";
import _ from "lodash";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArticleCategory,
  CreateArticleCategoryInput,
  UpdateArticleCategoryInput,
} from "../../api/schema";
import { FlexContainer } from "../../components/flex/Flex";
import {
  DeleteModal,
  DeleteStateHelper,
} from "../../components/form/DeleteModal";
import { ViewHeader } from "../../components/form/Header";
import {
  UpdateStateHelper,
  ValuesVariables,
} from "../../components/form/InputForm";
import { Table } from "../../components/Table";
import { UserLanguage } from "../../lang/lang";
import {
  ArticleCategoryListQuery,
  ArticleCategoryListResponse,
  CreateArticleCategoryMutation,
  CreateArticleCategoryResponse,
  DeleteArticleCategoryMutation,
  DeleteArticleCategoryResponse,
  UpdateArticleCategoryMutation,
  UpdateArticleCategoryResponse,
} from "./graphql";
import {
  CreateArticleCategoryModal,
  UpdateArticleCategoryModal,
} from "./Modals";

export const ArticleCategoryListView = (props: any) => {
  const [articleCategoryList, setArticleCategoryList] = useState<
    ArticleCategory[]
  >([]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalState, setUpdateModalState] = useState<
    UpdateStateHelper<ArticleCategory>
  >({ open: false, item: undefined });

  const [deleteModalState, setDeleteModalState] = useState<DeleteStateHelper>({
    open: false,
    id: undefined,
    prettyPrint: undefined,
  });

  const [createArticleCategory] = useMutation<
    CreateArticleCategoryResponse,
    ValuesVariables<CreateArticleCategoryInput>
  >(CreateArticleCategoryMutation);

  const [updateArticleCategory] = useMutation<
    UpdateArticleCategoryResponse,
    ValuesVariables<UpdateArticleCategoryInput>
  >(UpdateArticleCategoryMutation);

  const [deleteArticleCategory] = useMutation<DeleteArticleCategoryResponse>(
    DeleteArticleCategoryMutation
  );

  const updateArticleCategoryList = (data: ArticleCategory[]) =>
    setArticleCategoryList(data);

  const listQuery = useQuery<ArticleCategoryListResponse>(
    ArticleCategoryListQuery,
    {
      onCompleted: (data) => updateArticleCategoryList(data.articleCategories),
      fetchPolicy: "no-cache",
    }
  );

  const refetch = () => {
    listQuery.refetch().then((data) => {
      if (data.data) updateArticleCategoryList(data.data.articleCategories);
    });
  };

  return (
    <FlexContainer>
      <Card elevation={3}>
        <ViewHeader
          title={UserLanguage().pages.articleCategory.list}
          buttonText={UserLanguage().actions.create}
          open={setCreateModalOpen}
        />
        <CreateArticleCategoryModal
          bundle={{
            open: createModalOpen,
            onClose: () => setCreateModalOpen(false),
            onSuccess: refetch,
            mutation: createArticleCategory,
          }}
        />
        <UpdateArticleCategoryModal
          bundle={{
            modalState: updateModalState,
            mutation: updateArticleCategory,
            onClose: () =>
              setUpdateModalState({ open: false, item: undefined }),
            onSuccess: refetch,
          }}
        />
        <DeleteModal<DeleteArticleCategoryResponse>
          id={deleteModalState.id}
          open={deleteModalState.open}
          mutation={(id) => deleteArticleCategory({ variables: { id } })}
          texts={UserLanguage().pages.articleCategory.actions}
          resetOnClose
          onClose={() => {
            setDeleteModalState({ open: false, id: undefined });
          }}
          onSuccess={() => {
            setDeleteModalState({ open: false, id: undefined });
            refetch();
          }}
          type={UserLanguage().data.location.label}
          prettyPrint={deleteModalState.prettyPrint}
        />
        <Table<ArticleCategory>
          items={articleCategoryList}
          loading={listQuery.loading}
          columns={{
            id: { label: UserLanguage().data.id },
            name: {
              label: UserLanguage().data.name,
              render: (i) =>
                _.get(UserLanguage().data.article_categories, i.name, i.name),
            },
            actions: {
              label: UserLanguage().actions.label,
              render: (item) => (
                <ButtonGroup>
                  <Link to={`/article/category/${item.id}`}>
                    <Button intent="primary" rightIcon="edit">
                      {UserLanguage().actions.edit}
                    </Button>
                  </Link>
                  <Button
                    intent="danger"
                    rightIcon="delete"
                    onClick={() =>
                      setDeleteModalState({
                        open: true,
                        id: item.id,
                        prettyPrint: `${UserLanguage().data.name}: ${
                          item.name
                        }`,
                      })
                    }
                  >
                    {UserLanguage().actions.delete}
                  </Button>
                </ButtonGroup>
              ),
            },
          }}
        />
      </Card>
    </FlexContainer>
  );
};
